import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { communicationService, Message, Notification } from '../services/communicationService';
import { useNotificationHelpers } from './NotificationSystem';
import LoadingSpinner from './LoadingSpinner';
import Modal from './Modal';

interface ClientMessagingProps {
  isOpen: boolean;
  onClose: () => void;
}

const ClientMessaging: React.FC<ClientMessagingProps> = ({ isOpen, onClose }) => {
  const { userProfile } = useAuth();
  const { showSuccess, showError } = useNotificationHelpers();
  const [activeTab, setActiveTab] = useState<'messages' | 'notifications'>('messages');
  const [messages, setMessages] = useState<Message[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [showMessageModal, setShowMessageModal] = useState(false);

  useEffect(() => {
    if (isOpen && userProfile?.email) {
      loadData();
    }
  }, [isOpen, userProfile?.email]);

  const loadData = async () => {
    if (!userProfile?.email) return;

    setIsLoading(true);
    try {
      const [messagesData, notificationsData] = await Promise.all([
        communicationService.getMessagesForUser(userProfile.email),
        communicationService.getNotificationsForUser(userProfile.email)
      ]);

      setMessages(messagesData);
      setNotifications(notificationsData);
    } catch (error) {
      console.error('Error loading communication data:', error);
      showError('Loading Error', 'Failed to load messages and notifications');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMessageClick = async (message: Message) => {
    setSelectedMessage(message);
    setShowMessageModal(true);

    // Mark as read if not already read
    if (!message.isRead && message.id) {
      await communicationService.markMessageAsRead(message.id);
      // Update local state
      setMessages(prev => prev.map(m => 
        m.id === message.id ? { ...m, isRead: true } : m
      ));
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read if not already read
    if (!notification.isRead && notification.id) {
      await communicationService.markNotificationAsRead(notification.id);
      // Update local state
      setNotifications(prev => prev.map(n => 
        n.id === notification.id ? { ...n, isRead: true } : n
      ));
    }
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'quote_received':
        return '📋';
      case 'quote_approved':
        return '✅';
      case 'invoice_generated':
        return '💰';
      case 'payment_received':
        return '💳';
      case 'message_received':
        return '💬';
      default:
        return '📢';
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Messages & Notifications">
      <div className="w-full max-w-4xl mx-auto">
        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('messages')}
            className={`px-6 py-3 font-medium text-sm ${
              activeTab === 'messages'
                ? 'border-b-2 border-avis-blue text-avis-blue'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Messages ({messages.length})
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`px-6 py-3 font-medium text-sm ${
              activeTab === 'notifications'
                ? 'border-b-2 border-avis-blue text-avis-blue'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Notifications ({notifications.length})
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="lg" message="Loading..." />
          </div>
        ) : (
          <>
            {/* Messages Tab */}
            {activeTab === 'messages' && (
              <div className="space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <div className="text-4xl mb-4">📭</div>
                    <p>No messages yet</p>
                    <p className="text-sm">You'll receive messages from our team here</p>
                  </div>
                ) : (
                  messages.map((message) => (
                    <div
                      key={message.id}
                      onClick={() => handleMessageClick(message)}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        message.isRead 
                          ? 'bg-white border-gray-200 hover:bg-gray-50' 
                          : 'bg-blue-50 border-blue-200 hover:bg-blue-100'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-semibold text-gray-900">{message.subject}</h3>
                            {!message.isRead && (
                              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            From: {message.fromUserName} ({message.fromUserEmail})
                          </p>
                          <p className="text-gray-700 mt-2 line-clamp-2">
                            {message.message}
                          </p>
                        </div>
                        <div className="text-right text-sm text-gray-500 ml-4">
                          {formatDate(message.timestamp)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="space-y-4">
                {notifications.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <div className="text-4xl mb-4">🔔</div>
                    <p>No notifications yet</p>
                    <p className="text-sm">You'll receive updates about your projects here</p>
                  </div>
                ) : (
                  notifications.map((notification) => (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        notification.isRead 
                          ? 'bg-white border-gray-200 hover:bg-gray-50' 
                          : 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="text-2xl">{getNotificationIcon(notification.type)}</div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-semibold text-gray-900">{notification.title}</h3>
                            {!notification.isRead && (
                              <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                            )}
                          </div>
                          <p className="text-gray-700 mt-1">{notification.message}</p>
                          <p className="text-sm text-gray-500 mt-2">
                            {formatDate(notification.timestamp)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Message Detail Modal */}
      {showMessageModal && selectedMessage && (
        <Modal
          isOpen={showMessageModal}
          onClose={() => setShowMessageModal(false)}
          title={selectedMessage.subject}
        >
          <div className="space-y-4">
            <div className="border-b pb-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-gray-600">
                    From: <span className="font-medium">{selectedMessage.fromUserName}</span>
                  </p>
                  <p className="text-sm text-gray-600">
                    Email: {selectedMessage.fromUserEmail}
                  </p>
                </div>
                <div className="text-sm text-gray-500">
                  {formatDate(selectedMessage.timestamp)}
                </div>
              </div>
            </div>
            
            <div className="prose max-w-none">
              <p className="whitespace-pre-wrap text-gray-700">
                {selectedMessage.message}
              </p>
            </div>

            {selectedMessage.relatedQuoteId && (
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-blue-700">
                  Related to Quote ID: {selectedMessage.relatedQuoteId}
                </p>
              </div>
            )}

            {selectedMessage.relatedInvoiceId && (
              <div className="bg-green-50 p-3 rounded-lg">
                <p className="text-sm text-green-700">
                  Related to Invoice ID: {selectedMessage.relatedInvoiceId}
                </p>
              </div>
            )}
          </div>
        </Modal>
      )}
    </Modal>
  );
};

export default ClientMessaging;
